import { useEffect, useState } from 'react';
import { api, type ClientSession } from '../api';
import { DragonflyMark } from '../components/Brand';

export function InvitationPage({ session }: { session: ClientSession | null }) {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [checkingSession, setCheckingSession] = useState(!session?.user);
  useEffect(() => {
    if (session?.user) {
      setCheckingSession(false);
      return;
    }
    let cancelled = false;
    setCheckingSession(true);
    void (async () => {
      try {
        const { authClient } = await import('../auth-client');
        const result = await authClient.getSession();
        if (cancelled) return;
        if (result.error) throw new Error(result.error.message || 'Could not check your sign-in');
        // This identity only enables redemption. The server grants access after acceptance.
        setPendingEmail(result.data?.user.emailVerified ? result.data.user.email : null);
      } catch (cause) {
        if (!cancelled)
          setError(cause instanceof Error ? cause.message : 'Could not check your sign-in');
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user]);
  const signedInEmail = session?.user?.email || pendingEmail;
  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      await api.acceptInvitation(token);
      setAccepted(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The invitation could not be accepted');
    } finally {
      setBusy(false);
    }
  };
  const returnPath = `/invite?token=${encodeURIComponent(token)}`;
  return (
    <main id="main-content" className="login-page">
      <section className="login-card">
        <DragonflyMark />
        <span className="eyebrow">Calendar access</span>
        <h1>{accepted ? 'Invitation accepted' : 'Join a calendar'}</h1>
        {!token ? (
          <p className="inline-error" role="alert">
            This invitation link is incomplete.
          </p>
        ) : accepted ? (
          <>
            <p>Your calendar access is ready.</p>
            <a className="button" href="/">
              Open the calendar
            </a>
          </>
        ) : checkingSession ? (
          <p role="status">Checking your sign-in…</p>
        ) : !signedInEmail ? (
          <>
            <p>Sign in with the same email address that received this invitation.</p>
            <a className="button" href={`/login?callbackURL=${encodeURIComponent(returnPath)}`}>
              Continue to sign in
            </a>
          </>
        ) : (
          <>
            <p>
              Signed in as <b>{signedInEmail}</b>. Accepting adds the invited calendar to this
              account.
            </p>
            <button className="button" disabled={busy} onClick={() => void accept()}>
              {busy ? 'Accepting…' : 'Accept invitation'}
            </button>
          </>
        )}
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}
